"use client";
import { useTranslations } from "@zapier/i18n";

import { useState } from "react";

import {
  Button,
  Checkbox,
  Link,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
} from "@zapier/design-system";

import {
  getMessageFromNetworkError,
  useMoveZapsToFolder,
} from "@zapier/client";
import {
  useNotification,
  HIDE_MOVE_ZAP_WARNING,
  FOLDER_TRASH_ID,
} from "@zapier/utils";

import styles from "./MoveToSharedFolderModal.module.css";

interface Props {
  currentFolderId?: string;
  assetIds: string[];
  assetTitle: string;
  isOpen: boolean;
  isAssetOwnedByUser?: boolean;
  onSettled?: () => void;
  selectedFolderId: string;
  selectedFolderName: string;
  setIsOpen: (isOpen: boolean) => void;
  assetCount: number;
}

interface ContentProps extends Props {
  onMove: () => void;
  isCheckboxChecked?: boolean;
  setIsCheckboxChecked: (isChecked: boolean) => void;
}

interface onMoveOptions {
  selectedFolderId: string;
  selectedFolderName: string;
}

function MoveToSharedFolderContent({
  assetTitle,
  isCheckboxChecked,
  isOpen,
  onMove,
  setIsCheckboxChecked,
  setIsOpen,
  assetCount,
}: ContentProps) {
  const t = useTranslations("move-to-shared-folder-modal");

  return (
    <ModalDialogRoot
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      className={styles.modalContent}
    >
      <ModalDialogHeader textAlign="start">
        {t("headings.move-confirmation", { assetTitle: assetTitle })}
      </ModalDialogHeader>
      <ModalDialogBody textAlign="start">
        {t("descriptions.shared-folder-warning", { assetCount: assetCount })}
        <Checkbox
          isSelected={isCheckboxChecked}
          value="hide warning"
          name="checkbox"
          onChange={(isChecked) => {
            setIsCheckboxChecked(isChecked);
          }}
        >
          {t("checkboxes.dont-show-again")}
        </Checkbox>
      </ModalDialogBody>
      <ModalDialogFooter>
        <div className={styles.ctaButtons}>
          <Button variant="secondary" onPress={() => setIsOpen(false)}>
            {t("buttons.cancel")}
          </Button>

          <Button onPress={() => onMove()}>
            {t("buttons.move-assets", { assetCount: assetCount })}
          </Button>
        </div>
      </ModalDialogFooter>
    </ModalDialogRoot>
  );
}

export const MoveToSharedFolderModal = ({
  currentFolderId,
  assetIds,
  assetTitle,
  isOpen,
  isAssetOwnedByUser,
  onSettled,
  selectedFolderId,
  selectedFolderName,
  setIsOpen,
  assetCount,
}: Props) => {
  const t = useTranslations("move-to-shared-folder-modal");

  const [isCheckboxChecked, setIsCheckboxChecked] = useState<boolean>(false);
  const { moveZapsToFolder } = useMoveZapsToFolder();
  const { createNotification } = useNotification();

  {
    /* 
    Don't allow undo 
    1. From trash
    2. If the zap is not owned by the user and user is moving the zap FROM a personal folder
    */
  }
  const showUndoMoveZapAction =
    currentFolderId !== FOLDER_TRASH_ID && isAssetOwnedByUser;

  // This callback needs to happen outside of the modal so that it can
  // still display a notification when the request completes after the modal has closed.
  const handleMoveZapsToFolder = () => {
    localStorage.setItem(HIDE_MOVE_ZAP_WARNING, isCheckboxChecked.toString());

    moveZapsToFolder(
      {
        folderId: selectedFolderId,
        zapIds: assetIds,
      },
      {
        onSuccess: () => {
          createNotification({
            message: (
              <Text>
                {t("notifications.move-success", {
                  assetTitle: assetTitle,
                  selectedFolderName: selectedFolderName,
                })}{" "}
                {showUndoMoveZapAction && (
                  <Link
                    onPress={() =>
                      currentFolderId &&
                      handleUndoMoveZapsToFolder({
                        selectedFolderName,
                        selectedFolderId: currentFolderId,
                      })
                    }
                  >
                    {t("links.undo-action")}
                  </Link>
                )}
              </Text>
            ),
            type: "success",
          });
        },
        onError: (error: any) => {
          createNotification({
            title: `Unable to move ${assetTitle} to the ${selectedFolderName} folder`,
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
        onSettled: () => {
          onSettled?.();
        },
      },
    );

    setIsOpen(false);
  };

  const handleUndoMoveZapsToFolder = ({
    selectedFolderName,
    selectedFolderId,
  }: onMoveOptions) => {
    moveZapsToFolder(
      {
        folderId: selectedFolderId,
        zapIds: assetIds,
      },
      {
        onSuccess: () => {
          createNotification({
            message: (
              <Text>
                {t("notifications.undo-success", {
                  assetTitle: assetTitle,
                  selectedFolderName: selectedFolderName,
                })}
              </Text>
            ),
            type: "success",
          });
        },
        onError: (error: any) => {
          createNotification({
            title: `Unable to undo move for ${assetTitle} from the ${selectedFolderName} folder`,
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
        onSettled: () => {
          onSettled?.();
        },
      },
    );

    setIsOpen(false);
  };

  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return;
  }

  return (
    <MoveToSharedFolderContent
      assetIds={assetIds}
      assetTitle={assetTitle}
      isCheckboxChecked={isCheckboxChecked}
      isOpen={isOpen}
      onMove={handleMoveZapsToFolder}
      selectedFolderId={selectedFolderId}
      selectedFolderName={selectedFolderName}
      setIsCheckboxChecked={setIsCheckboxChecked}
      setIsOpen={setIsOpen}
      assetCount={assetCount}
    />
  );
};
